import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Copy,
  Trash2,
  Globe,
  GlobeLock,
  ArrowLeft,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';

type DungeonTheme = 'fortress' | 'crypt' | 'lair' | 'ruins' | 'sewer';
type DungeonStatus = 'draft' | 'published';

interface Dungeon {
  id: string;
  name: string;
  theme: DungeonTheme;
  status: DungeonStatus;
  updatedAt: string;
  mapData?: unknown;
}

// ---------------------------------------------------------------------------
// CreateDungeonDialog
// ---------------------------------------------------------------------------

interface CreateDungeonDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (dungeon: Dungeon) => void;
}

function CreateDungeonDialog({ open, onClose, onCreate }: CreateDungeonDialogProps) {
  const [name, setName] = useState('');
  const [theme, setTheme] = useState<DungeonTheme>('fortress');
  const [gridWidth, setGridWidth] = useState(60);
  const [gridHeight, setGridHeight] = useState(45);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setName('');
    setTheme('fortress');
    setGridWidth(60);
    setGridHeight(45);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/dungeons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, theme, gridWidth, gridHeight }),
      });
      const data = await res.json();
      onCreate(data.dungeon ?? data);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create dungeon.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-md rounded-xl bg-gray-900 border border-gray-800 shadow-2xl">
        <div className="px-6 py-5 border-b border-gray-800">
          <h2 className="text-lg font-semibold text-gray-100">New Dungeon</h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-950/40 border border-red-800 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-300">Name</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="The Sunken Crypt"
              className="w-full rounded-lg bg-gray-800 border border-gray-700 text-gray-100 placeholder-gray-500 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-300">Theme</label>
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as DungeonTheme)}
              className="w-full rounded-lg bg-gray-800 border border-gray-700 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="fortress">Fortress</option>
              <option value="crypt">Crypt</option>
              <option value="lair">Lair</option>
              <option value="ruins">Ruins</option>
              <option value="sewer">Sewer</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-300">Grid Width</label>
              <input
                type="number"
                min={10}
                max={200}
                value={gridWidth}
                onChange={(e) => setGridWidth(Number(e.target.value))}
                className="w-full rounded-lg bg-gray-800 border border-gray-700 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-300">Grid Height</label>
              <input
                type="number"
                min={10}
                max={200}
                value={gridHeight}
                onChange={(e) => setGridHeight(Number(e.target.value))}
                className="w-full rounded-lg bg-gray-800 border border-gray-700 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Create Dungeon
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DeleteConfirmDialog
// ---------------------------------------------------------------------------

interface DeleteConfirmDialogProps {
  dungeonName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirmDialog({ dungeonName, onConfirm, onCancel }: DeleteConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-sm rounded-xl bg-gray-900 border border-gray-800 shadow-2xl px-6 py-5 space-y-4">
        <h2 className="text-base font-semibold text-gray-100">Delete dungeon?</h2>
        <p className="text-sm text-gray-400">
          <span className="text-gray-200 font-medium">{dungeonName}</span> will be permanently
          deleted. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-red-700 text-white hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DungeonList page
// ---------------------------------------------------------------------------

export function DungeonList() {
  const navigate = useNavigate();

  const [dungeons, setDungeons] = useState<Dungeon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Dungeon | null>(null);
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const fetchDungeons = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/dungeons');
      const data = await res.json();
      setDungeons(data.dungeons ?? data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dungeons.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDungeons();
  }, []);

  const setBusy = (id: string, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      busy ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const setActionError = (id: string, msg: string) => {
    setActionErrors((prev) => ({ ...prev, [id]: msg }));
  };

  const clearActionError = (id: string) => {
    setActionErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // --- Handlers ---

  const handleCreated = (dungeon: Dungeon) => {
    navigate(`/admin/dungeons/${dungeon.id}/edit`);
  };

  const handleDuplicate = async (dungeon: Dungeon) => {
    setBusy(dungeon.id, true);
    clearActionError(dungeon.id);
    try {
      // Load full dungeon data
      const res = await apiFetch(`/api/admin/dungeons/${dungeon.id}`);
      const data = await res.json();
      const source: Dungeon = data.dungeon ?? data;

      // Create new dungeon
      const createRes = await apiFetch('/api/admin/dungeons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${source.name} (copy)`,
          theme: source.theme,
        }),
      });
      const createData = await createRes.json();
      const newDungeon: Dungeon = createData.dungeon ?? createData;

      // Save mapData if present
      if (source.mapData) {
        await apiFetch(`/api/admin/dungeons/${newDungeon.id}/map`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mapData: source.mapData }),
        });
      }

      navigate(`/admin/dungeons/${newDungeon.id}/edit`);
    } catch (err) {
      setActionError(dungeon.id, err instanceof Error ? err.message : 'Duplicate failed.');
    } finally {
      setBusy(dungeon.id, false);
    }
  };

  const handleDelete = async (dungeon: Dungeon) => {
    setBusy(dungeon.id, true);
    clearActionError(dungeon.id);
    try {
      await apiFetch(`/api/admin/dungeons/${dungeon.id}`, { method: 'DELETE' });
      setDungeons((prev) => prev.filter((d) => d.id !== dungeon.id));
    } catch (err) {
      setActionError(dungeon.id, err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setBusy(dungeon.id, false);
      setDeleteTarget(null);
    }
  };

  const handleTogglePublish = async (dungeon: Dungeon) => {
    setBusy(dungeon.id, true);
    clearActionError(dungeon.id);
    const endpoint =
      dungeon.status === 'published'
        ? `/api/admin/dungeons/${dungeon.id}/unpublish`
        : `/api/admin/dungeons/${dungeon.id}/publish`;
    try {
      const res = await apiFetch(endpoint, { method: 'POST' });
      const data = await res.json();
      const updated: Dungeon = data.dungeon ?? data;
      setDungeons((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    } catch (err) {
      setActionError(dungeon.id, err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setBusy(dungeon.id, false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-100 mb-2">Something went wrong</h2>
          <p className="text-gray-400 text-sm mb-6">{error}</p>
          <button
            onClick={fetchDungeons}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-100 rounded-lg text-sm hover:bg-gray-700 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-4">
          <Link
            to="/admin"
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-200 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Admin
          </Link>
          <span className="text-gray-700">/</span>
          <h1 className="text-sm font-medium text-gray-200">Dungeons</h1>

          <div className="ml-auto">
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-500 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Dungeon
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {dungeons.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-gray-600" />
            </div>
            <h2 className="text-lg font-medium text-gray-200 mb-2">No dungeons yet</h2>
            <p className="text-gray-500 text-sm mb-6">
              Create your first dungeon to get started.
            </p>
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-500 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Dungeon
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 text-gray-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Name</th>
                  <th className="px-4 py-3 text-left font-medium">Theme</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Updated</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {dungeons.map((dungeon) => {
                  const busy = busyIds.has(dungeon.id);
                  const actionError = actionErrors[dungeon.id];
                  const updatedDate =
                    typeof dungeon.updatedAt === 'string'
                      ? new Date(dungeon.updatedAt)
                      : dungeon.updatedAt;

                  return (
                    <>
                      <tr
                        key={dungeon.id}
                        className={cn(
                          'bg-gray-950 hover:bg-gray-900/60 transition-colors',
                          busy && 'opacity-60'
                        )}
                      >
                        {/* Name */}
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/dungeons/${dungeon.id}/edit`}
                            className="font-medium text-gray-100 hover:text-indigo-400 transition-colors"
                          >
                            {dungeon.name}
                          </Link>
                        </td>

                        {/* Theme */}
                        <td className="px-4 py-3 text-gray-400 capitalize">{dungeon.theme}</td>

                        {/* Status badge */}
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                              dungeon.status === 'published'
                                ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                                : 'bg-gray-800 text-gray-400 border border-gray-700'
                            )}
                          >
                            {dungeon.status}
                          </span>
                        </td>

                        {/* Updated date */}
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {updatedDate instanceof Date && !isNaN(updatedDate.getTime())
                            ? updatedDate.toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {/* Edit */}
                            <button
                              onClick={() => navigate(`/admin/dungeons/${dungeon.id}/edit`)}
                              disabled={busy}
                              title="Edit"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors disabled:opacity-40"
                            >
                              <Edit className="w-4 h-4" />
                            </button>

                            {/* Duplicate */}
                            <button
                              onClick={() => handleDuplicate(dungeon)}
                              disabled={busy}
                              title="Duplicate"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors disabled:opacity-40"
                            >
                              <Copy className="w-4 h-4" />
                            </button>

                            {/* Publish / Unpublish */}
                            <button
                              onClick={() => handleTogglePublish(dungeon)}
                              disabled={busy}
                              title={dungeon.status === 'published' ? 'Unpublish' : 'Publish'}
                              className={cn(
                                'p-1.5 rounded-lg transition-colors disabled:opacity-40',
                                dungeon.status === 'published'
                                  ? 'text-emerald-400 hover:text-emerald-200 hover:bg-gray-800'
                                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                              )}
                            >
                              {dungeon.status === 'published' ? (
                                <GlobeLock className="w-4 h-4" />
                              ) : (
                                <Globe className="w-4 h-4" />
                              )}
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => setDeleteTarget(dungeon)}
                              disabled={busy}
                              title="Delete"
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors disabled:opacity-40"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>

                            {/* Spinner when busy */}
                            {busy && <Loader2 className="w-4 h-4 animate-spin text-gray-500 ml-1" />}
                          </div>
                        </td>
                      </tr>

                      {/* Inline action error */}
                      {actionError && (
                        <tr key={`${dungeon.id}-error`} className="bg-red-950/20">
                          <td colSpan={5} className="px-4 py-2">
                            <div className="flex items-center gap-2 text-xs text-red-400">
                              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                              {actionError}
                              <button
                                onClick={() => clearActionError(dungeon.id)}
                                className="ml-auto text-gray-500 hover:text-gray-300"
                              >
                                Dismiss
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Dialogs */}
      <CreateDungeonDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreated}
      />

      {deleteTarget && (
        <DeleteConfirmDialog
          dungeonName={deleteTarget.name}
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
